import { Row } from 'components/Layout';
import ReviewPayments from 'components/ReviewPayments';
import TransactionAllocator from 'components/TransactionAllocator';
import { type Transaction } from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionTransactionSubTypes } from 'further-types/lp/action';
import { ActionRefLabels } from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { useState } from 'react';
import PayablesOrReceivablesHistory from './PayablesOrReceivablesHistory';
import ConfirmedPayablesOrReceivables from './ConfirmedPayablesOrReceivables';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import TabsPanel from 'further-ui/components/TabsPanel';
import { Column } from 'further-ui/components/Layout';
import usePostUnrealisedFxGainsData from './usePostUnrealisedFxGainsData';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  transactionChanges: Map<string, Array<Transaction>>;
  onTransactionChange: (
    rowId: string,
    transactions: Array<Transaction>,
  ) => void;
  onViewRecord?: (recordId: string) => void;
};

const PayablesOrReceivables: React.FC<Props> = ({
  transactionChanges,
  onTransactionChange,
  onViewRecord,
}) => {
  const { vehicle } = useLayoutContext();
  const {
    payablesData,
    payablesIsFetching,
    payablesPagination,
    isConfirmed,
    periodId,
  } = usePostUnrealisedFxGainsData();

  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  if (!vehicle) {
    return null;
  }

  if (isConfirmed) {
    return <ConfirmedPayablesOrReceivables onViewRecord={onViewRecord} />;
  }

  const rowsForTable = payablesData?.transactions.results ?? [];

  const columns: Array<TableColumn<(typeof rowsForTable)[number]>> = [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'transactionDate',
      label: 'Date',
      render: (row) => row.transactionDate.toLabel(),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
    {
      key: 'unreconciledAmountTcy',
      label: 'Original value (TCY)',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.unreconciledAmountTcy ??
            row.unreconciledAmountTcy,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'originalResidualValueLcy',
      label: 'Original value (LCY)',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.originalResidualValueLcy,
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'runningTotalUnrealisedLcy',
      label: 'Running total unrealised',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.runningTotalUnrealisedLcy,
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'currentValueLcy',
      label: 'Current value (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'unrealisedGainOrLossLcy',
      label: 'Unrealised gain/loss (LCY)',
      render: (row) => {
        return numberToCurrencyString(row.unrealisedGainOrLossLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      disableSorting: true,
    },
  ];

  const buildAllocatorTransactions = (
    row: (typeof rowsForTable)[number],
  ): Array<Transaction> => {
    const transactions = transactionChanges.get(row.id);
    if (transactions) {
      return transactions;
    }
    return row.transactions.map((transaction) => ({
      ...transaction,
      transactionCurrency: row.currencyCode,
      transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
    }));
  };

  return (
    <Column spacing="lg">
      {(payablesIsFetching || rowsForTable.length > 0) && (
        <Table
          columns={columns}
          rows={rowsForTable}
          isLoading={payablesIsFetching}
          pagination={{
            page: payablesPagination.page,
            rowsPerPage: payablesPagination.rowsPerPage,
            order: payablesPagination.order,
            orderBy: payablesPagination.orderBy,
            handleChangePage: payablesPagination.handleChangePage,
            handleChangeRowsPerPage: payablesPagination.handleChangeRowsPerPage,
          }}
          rowsTotalCount={payablesData?.transactions.totalCount ?? 0}
          isExpandableRow={() => true}
          isExpandedRow={(row) => expandedRowId === row.id}
          onRowExpand={(row) =>
            setExpandedRowId((prev) => (prev === row.id ? null : row.id))
          }
          getExpandedContent={(row) => (
            <ExpandedRowContainer>
              <TabsPanel
                tabs={[
                  {
                    key: 'accountTransactions',
                    label: 'Account transactions',
                    content: (
                      <Row fullWidth>
                        {!transactionTypesLoading && (
                          <TransactionAllocator
                            darkBackground
                            transactionTypeOptions={transactionTypeOptions}
                            transactions={buildAllocatorTransactions(row)}
                            onChange={(newTransactions) => {
                              onTransactionChange(
                                row.id ?? '',
                                newTransactions,
                              );
                            }}
                            transactionCurrency={row.currencyCode}
                            vehicle={vehicle}
                            fxRate={row.currencyRate}
                          />
                        )}
                      </Row>
                    ),
                  },
                  {
                    key: 'paymentRecord',
                    label: 'Payment record',
                    content: (
                      <Row fullWidth>
                        <ReviewPayments
                          transactionId={row.transactions[0]?.id}
                          vehicleId={vehicle._id}
                          currencyCode={row.currencyCode}
                          amountTcy={row.amountTcy}
                        />
                      </Row>
                    ),
                  },
                ]}
              />
            </ExpandedRowContainer>
          )}
        />
      )}
      <PayablesOrReceivablesHistory
        vehicleId={vehicle._id}
        periodId={periodId}
        onViewRecord={onViewRecord}
      />
    </Column>
  );
};

export default PayablesOrReceivables;
